'use client';

import { useEffect, useState } from 'react';

import { fetchEvent } from 'app-migration/api/api';
import Headline from 'DesignSystem3/Text/Headline';

import type { ProbabilityBarPieceVariant } from './probabilityBarPiece';
import ProbabilityBarPiece from './probabilityBarPiece';

interface ProbabilityBarProps {
    leftVariant: ProbabilityBarPieceVariant;
    rightVariant: ProbabilityBarPieceVariant;
    initialLeftProbability: number;
    eventTicker: string;
    marketTicker: string;
    withExtraTopPadding: boolean;
    marketPriceIsLeft?: boolean;
    polling?: boolean;
}

const textHoverColor = (variant: ProbabilityBarPieceVariant) => {
    return variant === 'neutral'
        ? 'group-hover:text-text-x10'
        : variant === 'red'
          ? 'group-hover:text-red-x40'
          : variant === 'purple'
            ? 'group-hover:text-purple-x40'
            : 'group-hover:text-blue-x40';
};

const ProbabilityBar = ({
    leftVariant,
    rightVariant,
    initialLeftProbability,
    withExtraTopPadding,
    eventTicker,
    marketTicker,
    marketPriceIsLeft = false,
    polling = true,
}: ProbabilityBarProps) => {
    const [leftProbability, setLeftProbility] = useState(initialLeftProbability);

    useEffect(() => {
        setLeftProbility(initialLeftProbability);
    }, [eventTicker, marketTicker]);

    useEffect(() => {
        if (!polling) return;

        const interval = setInterval(() => {
            fetchEvent(eventTicker).then((event) => {
                if (event) {
                    const market = event.markets.find((m) => m.ticker_name === marketTicker);
                    if (market) {
                        if (market.last_price === 0) {
                            const midSpread = Math.floor(market.yes_ask + market.yes_ask) / 2;
                            setLeftProbility(marketPriceIsLeft ? midSpread : 100 - midSpread);
                        }
                        setLeftProbility(marketPriceIsLeft ? market.last_price : 100 - market.last_price);
                    }
                }
            });
        }, 10000);

        return () => clearInterval(interval);
    }, []);

    const rightProbability = 100 - leftProbability;
    const gap = 0.5;
    const totalWidth = 100 - gap;
    const leftWidth = (leftProbability / 100) * totalWidth;
    const rightWidth = (rightProbability / 100) * totalWidth;

    const leftTextHoverColor = textHoverColor(leftVariant);
    const rightTextHoverColor = textHoverColor(rightVariant);

    return (
        <div className="flex relative items-center w-full">
            <div
                className={`item relative flex-grow group ${withExtraTopPadding ? 'mt-[-32px] pt-4' : ''}`}
                style={{
                    width: `${leftWidth}%`,
                    transition: 'width 300ms ease-in-out',
                }}
            >
                <ProbabilityBarPiece variant={leftVariant} />
                <div
                    className={`absolute inset-0 flex items-center pl-1 pointer-events-none ${leftTextHoverColor} ${
                        withExtraTopPadding ? 'mt-4' : ''
                    }`}
                >
                    <Headline.x10>{leftProbability}%</Headline.x10>
                </div>
            </div>
            <div className="w-0.25" />
            <div
                className={`item relative flex-grow group ${withExtraTopPadding ? 'mt-[-32px] pt-4' : ''}`}
                style={{ width: `${rightWidth}%`, transition: 'width 300ms ease-in-out' }}
            >
                <ProbabilityBarPiece variant={rightVariant} />
                <div
                    className={`absolute inset-0 flex items-center justify-end pr-1 pointer-events-none ${rightTextHoverColor} ${
                        withExtraTopPadding ? 'mt-4' : ''
                    }`}
                >
                    <Headline.x10>{rightProbability}%</Headline.x10>
                </div>
            </div>
        </div>
    );
};

export default ProbabilityBar;
